import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { YearbookElement, Position, Size, PAGE_WIDTH, PAGE_HEIGHT, cornerRadiiToCss, elementShadowToCss, PageGuide } from '@/types/yearbook';
import SvgSticker from './SvgSticker';
import { ensureFontLoaded, fontAllowsBold, fontAllowsItalic } from '@/lib/fonts';

// Only text elements have margins (5mm). All other elements (images, shapes, stickers) have no margins.
const MARGIN_TEXT = 14.1732;  // 5mm in points — texte uniquement
import SlotRenderer from './SlotRenderer';
import { computeSnap, computeResizeSnap, GuideLine } from '@/lib/alignmentGuides';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DraggableElementProps {
  element: YearbookElement;
  isSelected: boolean;
  isInMultiSelection?: boolean;
  onSelect: (id: string, pageIndex?: number, metaKey?: boolean) => void;
  onUpdate: (id: string, updates: Partial<YearbookElement>) => void;
  onDragUpdate: (id: string, updates: Partial<YearbookElement>) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  /** Ids of the group this element belongs to (used to drag the whole group even before selection state updates). */
  groupDragIds?: string[];
  onMultiDragStart?: (ids?: string[]) => void;
  onMultiDragUpdate?: (dx: number, dy: number) => void;
  scale: number;
  isTrombiPage?: boolean;
  allElements?: YearbookElement[];
  onGuidesChange?: (guides: GuideLine[]) => void;
  elementIndex?: number;
  pageIndex?: number;
  pageGuides?: PageGuide[];
  forceCropId?: number;
  mobileInteraction?: boolean;
}

const ShapeRenderer: React.FC<{ element: YearbookElement }> = ({ element }) => {
  const color = element.textStyle?.color || '#000000';
  const w = element.size.width;
  const h = element.size.height;
  const cr = element.cornerRadii;

  if (element.shapeType === 'circle') {
    return (
      <svg width={w} height={h} className="pointer-events-none">
        <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill={color} />
      </svg>
    );
  }
  if (element.shapeType === 'line') {
    return (
      <svg width={w} height={h} className="pointer-events-none">
        <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke={color} strokeWidth={element.strokeWidth ?? 2} />
      </svg>
    );
  }
  // Default: rect
  const rx = cr ? Math.max(cr.tl ?? 0, cr.tr ?? 0, cr.br ?? 0, cr.bl ?? 0) : 0;
  return (
    <svg width={w} height={h} className="pointer-events-none">
      <rect width={w} height={h} fill={color} rx={rx || undefined} ry={rx || undefined} />
    </svg>
  );
};

const DraggableElement: React.FC<DraggableElementProps> = ({
  element,
  isSelected,
  isInMultiSelection,
  onSelect,
  onUpdate,
  onDragUpdate,
  onDragStart,
  onDragEnd,
  groupDragIds,
  onMultiDragStart,
  onMultiDragUpdate,
  scale,
  isTrombiPage,
  allElements,
  onGuidesChange,
  elementIndex = 1,
  pageIndex,
  pageGuides,
  forceCropId,
  mobileInteraction = false,
}) => {
  // Compute siblings internally to avoid new array ref from parent on every render
  const siblingElements = useMemo(
    () => allElements?.filter((el) => el.id !== element.id),
    [allElements, element.id]
  );
  const elementRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const mobileTapRef = useRef<{ x: number; y: number; selected: boolean } | null>(null);
  const lastTouchRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropPanStart, setCropPanStart] = useState<Position | null>(null);

  // Allow external trigger of crop mode via forceCropId counter
  useEffect(() => {
    if (forceCropId && forceCropId > 0 && element.type === 'image' && !element.isSticker) {
      setIsCropping(true);
    }
  }, [forceCropId, element.type, element.isSticker]);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
  const initialPosRef = useRef<Position>({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState<{ pos: Position; size: Size; elementPos?: Position }>({
    pos: { x: 0, y: 0 },
    size: { width: 0, height: 0 },
  });
  const [resizeCorner, setResizeCorner] = useState<string>('');
  const rotateStartAngle = useRef(0);
  const rotateStartRotation = useRef(0);

  const isLocked = element.locked === true;
  const isTrombiText = isTrombiPage && element.type === 'text';
  const isFreeSlot = element.type === 'free_slot';
  const isRotatable = (element.type === 'image' || element.type === 'shape' || element.type === 'shape_border' || element.type === 'text') && !isLocked;

  const finishMobileTap = (x: number, y: number) => {
    const tap = mobileTapRef.current;
    mobileTapRef.current = null;
    if (!tap?.selected || Math.hypot(x - tap.x, y - tap.y) > 8 ||
        element.type !== 'text' || isLocked || isTrombiText || isEditing) return;
    // Focus within the user gesture so mobile browsers open their keyboard.
    flushSync(() => setIsEditing(true));
    textRef.current?.focus();
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (Date.now() - lastTouchRef.current < 700) return;
      if (isEditing || isLocked || isTrombiText || isCropping) return;
      e.stopPropagation();
      if (mobileInteraction) mobileTapRef.current = { x: e.clientX, y: e.clientY, selected: isSelected };
      const metaKey = e.metaKey || e.ctrlKey;

      if (isInMultiSelection && !metaKey) {
      } else {
        onSelect(element.id, pageIndex, metaKey);
      }

      if (metaKey) return;

      initialPosRef.current = { ...element.position };

      const groupIds = groupDragIds && groupDragIds.length > 1 ? groupDragIds : undefined;
      if ((isInMultiSelection || groupIds) && onMultiDragStart) {
        onMultiDragStart(groupIds);
      } else {
        onDragStart();
      }
      setIsDragging(true);
      setDragStart({
        x: e.clientX / scale - element.position.x,
        y: e.clientY / scale - element.position.y,
      });
    },
    [element.id, element.position, onSelect, onDragStart, onMultiDragStart, scale, isEditing, isLocked, isTrombiText, pageIndex, isInMultiSelection, isCropping, groupDragIds, mobileInteraction, isSelected]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      lastTouchRef.current = Date.now();
      if (isEditing || isLocked || isTrombiText || isCropping) return;
      if (e.touches.length !== 1) { mobileTapRef.current = null; return; }
      e.stopPropagation();
      const touch = e.touches[0];
      if (mobileInteraction) mobileTapRef.current = { x: touch.clientX, y: touch.clientY, selected: isSelected };
      onSelect(element.id, pageIndex, false);

      initialPosRef.current = { ...element.position };
      onDragStart();
      setIsDragging(true);
      setDragStart({
        x: touch.clientX / scale - element.position.x,
        y: touch.clientY / scale - element.position.y,
      });
    },
    [element.id, element.position, onSelect, onDragStart, scale, isEditing, isLocked, isTrombiText, pageIndex, isCropping, mobileInteraction, isSelected]
  );

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent, corner: string) => {
      if (isLocked) return;
      e.stopPropagation();
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      onDragStart();
      setIsResizing(true);
      setResizeCorner(corner);
      setResizeStart({
        pos: { x: e.clientX, y: e.clientY },
        size: { ...element.size },
        elementPos: { ...element.position },
      });
    },
    [element.size, element.position, onDragStart, isLocked]
  );

  const handleRotateMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isLocked) return;
      e.stopPropagation();
      e.preventDefault();
      onDragStart();
      setIsRotating(true);
      const el = elementRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
      rotateStartAngle.current = angle;
      rotateStartRotation.current = element.rotation ?? 0;
    },
    [isLocked, onDragStart, element.rotation]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rawX = e.clientX / scale - dragStart.x;
      const rawY = e.clientY / scale - dragStart.y;

      // Multi-drag: compute delta and delegate to parent
      if ((isInMultiSelection || (groupDragIds && groupDragIds.length > 1)) && onMultiDragUpdate) {
        const dx = rawX - initialPosRef.current.x;
        const dy = rawY - initialPosRef.current.y;
        onMultiDragUpdate(dx, dy);
        return;
      }

      // Compute rotated bounding box to clamp correctly
      const rot = (element.rotation ?? 0) * (Math.PI / 180);
      const w = element.size.width;
      const h = element.size.height;
      const bbW = Math.abs(w * Math.cos(rot)) + Math.abs(h * Math.sin(rot));
      const bbH = Math.abs(w * Math.sin(rot)) + Math.abs(h * Math.cos(rot));
      const offsetX = (bbW - w) / 2;
      const offsetY = (bbH - h) / 2;

      // Text elements: 5mm margin inside page. Others: allow up to half the element outside the page.
      let minX: number, minY: number, maxX: number, maxY: number;
      if (element.type === 'text') {
        const MARGIN = MARGIN_TEXT;
        minX = MARGIN + offsetX;
        minY = MARGIN + offsetY;
        maxX = PAGE_WIDTH - MARGIN - w - offsetX;
        maxY = PAGE_HEIGHT - MARGIN - h - offsetY;
      } else {
        const halfW = w / 2;
        const halfH = h / 2;
        minX = -halfW;
        minY = -halfH;
        maxX = PAGE_WIDTH - halfW;
        maxY = PAGE_HEIGHT - halfH;
      }
      const clampedX = Math.round(Math.min(Math.max(rawX, minX), maxX));
      const clampedY = Math.round(Math.min(Math.max(rawY, minY), maxY));

      if (siblingElements && onGuidesChange) {
        const snap = computeSnap(clampedX, clampedY, element.size.width, element.size.height, siblingElements, PAGE_WIDTH, PAGE_HEIGHT, pageGuides);
        const sx = Math.round(Math.min(Math.max(snap.x, minX), maxX));
        const sy = Math.round(Math.min(Math.max(snap.y, minY), maxY));
        onDragUpdate(element.id, { position: { x: sx, y: sy } });
        onGuidesChange(snap.guides);
      } else {
        onDragUpdate(element.id, { position: { x: clampedX, y: clampedY } });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      const rawX = touch.clientX / scale - dragStart.x;
      const rawY = touch.clientY / scale - dragStart.y;

      const rot = (element.rotation ?? 0) * (Math.PI / 180);
      const w = element.size.width;
      const h = element.size.height;
      const bbW = Math.abs(w * Math.cos(rot)) + Math.abs(h * Math.sin(rot));
      const bbH = Math.abs(w * Math.sin(rot)) + Math.abs(h * Math.cos(rot));
      const offsetX = (bbW - w) / 2;
      const offsetY = (bbH - h) / 2;

      let minX: number, minY: number, maxX: number, maxY: number;
      if (element.type === 'text') {
        const MARGIN = MARGIN_TEXT;
        minX = MARGIN + offsetX;
        minY = MARGIN + offsetY;
        maxX = PAGE_WIDTH - MARGIN - w - offsetX;
        maxY = PAGE_HEIGHT - MARGIN - h - offsetY;
      } else {
        const halfW = w / 2;
        const halfH = h / 2;
        minX = -halfW;
        minY = -halfH;
        maxX = PAGE_WIDTH - halfW;
        maxY = PAGE_HEIGHT - halfH;
      }
      const clampedX = Math.round(Math.min(Math.max(rawX, minX), maxX));
      const clampedY = Math.round(Math.min(Math.max(rawY, minY), maxY));
      onDragUpdate(element.id, { position: { x: clampedX, y: clampedY } });
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      onDragEnd();
      onGuidesChange?.([]);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      onDragEnd();
      onGuidesChange?.([]);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart, element.id, element.size.width, element.size.height, element.rotation, element.type, onDragUpdate, onDragEnd, scale, siblingElements, onGuidesChange, isInMultiSelection, onMultiDragUpdate, pageGuides, groupDragIds]);

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      const dx = (e.clientX - resizeStart.pos.x) / scale;
      const dy = (e.clientY - resizeStart.pos.y) / scale;

      const isSticker = element.type === 'image' && element.isSticker;
      const isCircleShape = element.type === 'shape' && element.shapeType === 'circle';
      const isCircleImage = element.type === 'image' && element.isCircle;
      const isQrCode = !!element.qrcodeId;
      const isHomothetic = isSticker || isCircleShape || isCircleImage || isQrCode;
      const aspectRatio = (isCircleShape || isCircleImage) ? 1 : resizeStart.size.width / resizeStart.size.height;

      let newWidth = resizeStart.size.width;
      let newHeight = resizeStart.size.height;

      if (isHomothetic) {
        const isRight = resizeCorner.includes('r');
        const isBottom = resizeCorner.includes('b');
        const rawDelta = (isRight ? dx : -dx);
        const rawDeltaY = (isBottom ? dy : -dy);
        const dominantDelta = Math.abs(rawDelta) >= Math.abs(rawDeltaY) ? rawDelta : rawDeltaY * aspectRatio;
        newWidth = Math.max(20, resizeStart.size.width + dominantDelta);
        newHeight = Math.round(newWidth / aspectRatio);
      } else {
        if (resizeCorner.includes('r')) newWidth = Math.max(40, resizeStart.size.width + dx);
        if (resizeCorner.includes('l')) newWidth = Math.max(40, resizeStart.size.width - dx);
        if (resizeCorner.includes('b')) newHeight = Math.max(20, resizeStart.size.height + dy);
        if (resizeCorner.includes('t')) newHeight = Math.max(20, resizeStart.size.height - dy);
      }

      // Compute new position based on corner
      const startPos = resizeStart.elementPos || element.position;
      let newX = startPos.x;
      let newY = startPos.y;

      if (resizeCorner.includes('l')) {
        newX = startPos.x + (resizeStart.size.width - newWidth);
      }
      if (resizeCorner.includes('t')) {
        newY = startPos.y + (resizeStart.size.height - newHeight);
      }

      // Apply snap guides during resize
      if (siblingElements && onGuidesChange) {
        const snap = computeResizeSnap(
          newX, newY, newWidth, newHeight,
          resizeCorner,
          siblingElements,
          PAGE_WIDTH, PAGE_HEIGHT,
          pageGuides
        );

        // For homothetic resize, if snap changed width, recompute height
        if (isHomothetic) {
          const snappedW = snap.width;
          const snappedH = Math.round(snappedW / aspectRatio);
          // Adjust position if resizing from left/top
          let sx = snap.x;
          let sy = snap.y;
          if (resizeCorner.includes('l')) {
            sx = startPos.x + resizeStart.size.width - snappedW;
          }
          if (resizeCorner.includes('t')) {
            sy = startPos.y + resizeStart.size.height - snappedH;
          }
          onDragUpdate(element.id, {
            size: { width: snappedW, height: snappedH },
            position: { x: Math.round(sx), y: Math.round(sy) },
          });
        } else {
          onDragUpdate(element.id, {
            size: { width: snap.width, height: snap.height },
            position: { x: snap.x, y: snap.y },
          });
        }
        onGuidesChange(snap.guides);
      } else {
        const updates: Partial<YearbookElement> = {
          size: { width: Math.round(newWidth), height: Math.round(newHeight) },
          position: { x: Math.round(newX), y: Math.round(newY) },
        };
        onDragUpdate(element.id, updates);
      }
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      onDragEnd();
      onGuidesChange?.([]);
      if (element.type === 'text') {
        setTimeout(() => autoFitTextSize(), 50);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isResizing, resizeStart, resizeCorner, element, onDragUpdate, onDragEnd, scale, siblingElements, onGuidesChange, pageGuides]);

  useEffect(() => {
    if (!isRotating) return;

    const handleMouseMove = (e: MouseEvent) => {
      const el = elementRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
      let delta = angle - rotateStartAngle.current;
      // Snap to 15° increments when Shift held
      if (e.shiftKey) {
        delta = Math.round(delta / 15) * 15;
      }
      const next = ((rotateStartRotation.current + delta) % 360 + 360) % 360;
      onDragUpdate(element.id, { rotation: Math.round(next) });
    };

    const handleMouseUp = () => {
      setIsRotating(false);
      onDragEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isRotating, element.id, onDragUpdate, onDragEnd]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (element.type === 'text') {
        e.stopPropagation();
        setIsEditing(true);
        requestAnimationFrame(() => {
          textRef.current?.focus();
        });
      } else if (element.type === 'image' && !element.isSticker) {
        e.stopPropagation();
        setIsCropping(true);
      }
    },
    [element.type, element.isSticker]
  );

  const autoFitTextSize = useCallback(() => {
    const el = textRef.current;
    if (!el || element.type !== 'text') return;
    requestAnimationFrame(() => {
      const scrollH = el.scrollHeight;
      const scrollW = el.scrollWidth;
      const updates: Partial<YearbookElement> = {};
      if (scrollH > element.size.height) {
        updates.size = { ...element.size, height: scrollH };
      }
      if (scrollW > element.size.width) {
        updates.size = { ...(updates.size || element.size), width: scrollW };
      }
      if (updates.size) {
        onUpdate(element.id, updates);
      }
    });
  }, [element.id, element.type, element.size, onUpdate]);

  const handleBlur = useCallback(() => {
    const text = textRef.current?.innerText || '';
    onUpdate(element.id, { content: text });
    setIsEditing(false);
    // Auto-fit after committing
    setTimeout(() => {
      const el = textRef.current;
      if (el) {
        const scrollH = el.scrollHeight;
        if (scrollH > element.size.height) {
          onUpdate(element.id, { size: { ...element.size, height: scrollH } });
        }
      }
    }, 50);
  }, [element.id, element.size, onUpdate]);

  const handleInput = useCallback(
    (_e: React.FormEvent<HTMLDivElement>) => {
      // Auto-expand height while typing
      const el = textRef.current;
      if (el && el.scrollHeight > element.size.height) {
        onUpdate(element.id, { size: { ...element.size, height: el.scrollHeight } });
      }
    },
    [element.id, element.size, onUpdate]
  );

  // Auto-fit when text style changes (font size, family, etc.)
  // Sync external content to DOM — only when not editing to avoid clobbering user input.
  // This replaces dangerouslySetInnerHTML to avoid React clearing innerHTML on re-render.
  useEffect(() => {
    if (!isEditing && textRef.current && element.type === 'text') {
      textRef.current.innerText = element.content || '';
    }
  }, [element.content, element.type, isEditing]);

  useEffect(() => {
    if (element.type === 'text') ensureFontLoaded(element.textStyle?.fontFamily);
  }, [element.type, element.textStyle?.fontFamily]);

  useEffect(() => {
    if (element.type === 'text' && !isEditing) {
      autoFitTextSize();
    }
  }, [element.textStyle?.fontSize, element.textStyle?.fontFamily, element.textStyle?.fontWeight, element.type]);

  // ---- Crop mode for free images ----
  const cropOffsetRef = useRef<Position>({ x: 50, y: 50 });

  // Exit crop mode on click-outside or Escape
  useEffect(() => {
    if (!isCropping) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        setIsCropping(false);
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (elementRef.current && !elementRef.current.contains(e.target as Node)) {
        setIsCropping(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    // Use timeout so the double-click that opened crop doesn't immediately close it
    const t = setTimeout(() => window.addEventListener('mousedown', handleClickOutside), 50);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(t);
    };
  }, [isCropping]);

  const getCropOverflow = useCallback((imgScale: number) => {
    const containerRatio = element.size.width / element.size.height;
    const imageRatio = element.imageRatio || containerRatio;
    let coverWidth = element.size.width;
    let coverHeight = element.size.height;

    if (imageRatio > containerRatio) {
      coverWidth = element.size.height * imageRatio;
    } else if (imageRatio < containerRatio) {
      coverHeight = element.size.width / imageRatio;
    }

    return {
      x: Math.max(0, coverWidth * imgScale - element.size.width),
      y: Math.max(0, coverHeight * imgScale - element.size.height),
    };
  }, [element.imageRatio, element.size.height, element.size.width]);

  // Crop pan drag
  useEffect(() => {
    if (!cropPanStart) return;
    const startOffset = { ...cropOffsetRef.current };

    const handleMouseMove = (e: MouseEvent) => {
      const imgScale = element.imageScale || 1;
      const { x: overflowX, y: overflowY } = getCropOverflow(imgScale);
      if (overflowX <= 0 && overflowY <= 0) return;

      const dx = (e.clientX - cropPanStart.x) / scale;
      const dy = (e.clientY - cropPanStart.y) / scale;

      // Convert pixel delta to percentage of overflow
      const pctDx = overflowX > 0 ? (dx / overflowX) * 100 : 0;
      const pctDy = overflowY > 0 ? (dy / overflowY) * 100 : 0;

      const newX = Math.min(100, Math.max(0, startOffset.x - pctDx));
      const newY = Math.min(100, Math.max(0, startOffset.y - pctDy));

      onDragUpdate(element.id, { imageOffset: { x: Math.round(newX), y: Math.round(newY) } });
    };

    const handleMouseUp = () => {
      setCropPanStart(null);
      onDragEnd();
      // Update ref for next drag
      cropOffsetRef.current = element.imageOffset || { x: 50, y: 50 };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [cropPanStart, element.id, element.imageScale, element.imageOffset, scale, onDragUpdate, onDragEnd, getCropOverflow]);

  // Crop wheel zoom
  const handleCropWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!isCropping) return;
      e.stopPropagation();
      e.preventDefault();
      const current = element.imageScale || 1;
      const delta = e.deltaY < 0 ? 0.1 : -0.1;
      const next = Math.min(4, Math.max(1, Math.round((current + delta) * 100) / 100));
      onUpdate(element.id, { imageScale: next });
    },
    [isCropping, element.imageScale, element.id, onUpdate]
  );

  const handleCropMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isCropping) return;
      e.stopPropagation();
      e.preventDefault();
      cropOffsetRef.current = element.imageOffset || { x: 50, y: 50 };
      setCropPanStart({ x: e.clientX, y: e.clientY });
      onDragStart();
    },
    [isCropping, element.imageOffset, onDragStart]
  );

  const resizeHandles = ['tl', 'tr', 'bl', 'br'];
  const handlePositions: Record<string, React.CSSProperties> = {
    tl: { top: -5, left: -5, cursor: 'nwse-resize' },
    tr: { top: -5, right: -5, cursor: 'nesw-resize' },
    bl: { bottom: -5, left: -5, cursor: 'nesw-resize' },
    br: { bottom: -5, right: -5, cursor: 'nwse-resize' },
  };

  // Slots are not draggable but image inside can be panned
  if (element.type === 'slot') {
    return (
      <SlotRenderer
        element={element}
        isSelected={isSelected}
        onSelect={() => onSelect(element.id, pageIndex)}
        onUpdate={onUpdate}
        onDragUpdate={onDragUpdate}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        scale={scale}
        elementIndex={elementIndex}
      />
    );
  }

  const imgSrc = element.imageUrl || element.thumbnail;
  const showSelected = isSelected || isInMultiSelection;

  return (
    <div
      data-element-id={element.id}
      ref={elementRef}
      className={`absolute group ${(isLocked || isTrombiText) ? 'cursor-default' : isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${
        showSelected ? 'element-selected' : 'hover:element-hover'
      }`}
      style={{
        left: element.position.x,
        top: element.position.y,
        width: element.size.width,
        height: element.size.height,
        transform: `rotate(${element.rotation}deg)`,
        zIndex: (isDragging || isResizing || isRotating) ? 1000 : (element.zIndex != null ? element.zIndex : (element.zPriority === 'front' ? 100 + elementIndex : element.zPriority === 'back' ? elementIndex : 10 + elementIndex)),
        backgroundColor: element.backgroundColor || undefined,
        borderRadius: (element.type === 'shape' || element.type === 'shape_border') ? cornerRadiiToCss(element.cornerRadii) || undefined : undefined,
        overflow: (element.type === 'shape' || element.type === 'shape_border') ? 'hidden' : undefined,
        border: element.borderColor ? `1px solid ${element.borderColor}` : undefined,
        boxShadow: elementShadowToCss(element.shadow),
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={mobileInteraction ? (e) => {
        const touch = e.touches[0];
        const tap = mobileTapRef.current;
        if (tap && (!touch || Math.hypot(touch.clientX - tap.x, touch.clientY - tap.y) > 8)) mobileTapRef.current = null;
      } : undefined}
      onTouchEnd={mobileInteraction ? (e) => {
        e.stopPropagation();
        lastTouchRef.current = Date.now();
        const touch = e.changedTouches[0];
        if (touch) finishMobileTap(touch.clientX, touch.clientY);
      } : undefined}
      onTouchCancel={() => { mobileTapRef.current = null; }}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => {
        e.stopPropagation();
        if (isLocked || isTrombiText) onSelect(element.id, pageIndex, e.metaKey || e.ctrlKey);
        else if (mobileInteraction && Date.now() - lastTouchRef.current >= 700) finishMobileTap(e.clientX, e.clientY);
      }}
    >
      {element.type === 'text' && (
        <div
          ref={textRef}
          contentEditable={isEditing}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onInput={handleInput}
          className="w-full h-full outline-none overflow-hidden"
          style={{
            fontSize: element.textStyle?.fontSize || 18,
            fontFamily: element.textStyle?.fontFamily || 'DM Sans',
            fontWeight: fontAllowsBold(element.textStyle?.fontFamily) ? element.textStyle?.fontWeight || '400' : '400',
            color: element.textStyle?.color || '#1a1a2e',
            textAlign: element.textStyle?.textAlign || 'left',
            fontStyle: element.textStyle?.italic && fontAllowsItalic(element.textStyle?.fontFamily) ? 'italic' : 'normal',
            textDecoration: element.textStyle?.underline ? 'underline' : 'none',
            cursor: isEditing ? 'text' : 'inherit',
            lineHeight: 1.4,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        />
      )}

      {element.type === 'text' && element.forbiddenWords && element.forbiddenWords.length > 0 && (
        <>
          <div
            className="absolute inset-0 pointer-events-none rounded-sm"
            style={{ outline: '2px dashed hsl(var(--destructive))', outlineOffset: '2px' }}
          />
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="absolute -top-2 -right-2 flex items-center justify-center rounded-full shadow-md pointer-events-auto cursor-help hover:scale-110 transition-transform"
                style={{
                  width: 18,
                  height: 18,
                  backgroundColor: 'hsl(var(--destructive))',
                  color: 'hsl(var(--destructive-foreground))',
                  zIndex: 2,
                  border: 'none',
                  padding: 0,
                }}
                aria-label={`Mots interdits détectés : ${element.forbiddenWords.join(', ')}`}
              >
                <AlertTriangle size={12} strokeWidth={2.5} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={6} className="max-w-xs border-destructive/30 bg-popover/95 backdrop-blur-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Mots interdits détectés</p>
                  <p className="text-xs text-muted-foreground mt-1">{element.forbiddenWords.join(', ')}</p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </>
      )}

      {element.type === 'image' && imgSrc && (() => {
        const imgScale = element.imageScale || 1;
        const offset = element.imageOffset || { x: 50, y: 50 };
        const hasCrop = isCropping || !!element.imageOffset || imgScale > 1;
        const isSvgSticker = element.isSticker && (element.stickerFormat === 'svg' || element.stickerFormat === 'simple_svg');
        return (
          <div
            className="w-full h-full overflow-hidden relative"
            style={{
              borderRadius: cornerRadiiToCss(element.cornerRadii) || (element.isCircle ? '50%' : element.borderRadius ? `${element.borderRadius}px` : '0.125rem'),
              cursor: isCropping ? (cropPanStart ? 'grabbing' : 'grab') : undefined,
            }}
            onMouseDown={isCropping ? handleCropMouseDown : undefined}
            onWheel={isCropping ? handleCropWheel : undefined}
          >
            {isSvgSticker ? (
              <SvgSticker url={imgSrc} color={element.stickerColor} />
            ) : (
              <img
                src={imgSrc}
                alt="Yearbook element"
                className={`w-full h-full ${isCropping ? '' : 'pointer-events-none'}`}
                style={{
                  objectFit: (element.forceDimension && !hasCrop) ? 'fill' : 'cover',
                  objectPosition: hasCrop ? `${offset.x}% ${offset.y}%` : undefined,
                  transform: imgScale > 1 ? `scale(${imgScale})` : undefined,
                  transformOrigin: hasCrop ? `${offset.x}% ${offset.y}%` : undefined,
                }}
                draggable={false}
              />
            )}
            {/* Crop mode overlay */}
            {isCropping && (
              <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 0 2px hsl(var(--editor-sidebar-active))', borderRadius: 'inherit' }}>
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-2 pointer-events-auto text-xs shadow-md border border-border">
                  <span className="text-muted-foreground font-mono">{Math.round(imgScale * 100)}%</span>
                  <button
                    className="text-muted-foreground hover:text-foreground transition-colors text-[10px] uppercase tracking-wide"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdate(element.id, { imageScale: 1, imageOffset: { x: 50, y: 50 } });
                    }}
                  >
                    Reset
                  </button>
                  <button
                    className="text-muted-foreground hover:text-foreground transition-colors text-[10px] uppercase tracking-wide"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCropping(false);
                    }}
                  >
                    ✓
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {element.type === 'shape' && <ShapeRenderer element={element} />}

      {element.type === 'shape_border' && (
        <div
          className="w-full h-full pointer-events-none"
          style={{ border: `2px solid ${element.textStyle?.color || '#000000'}`, borderRadius: cornerRadiiToCss(element.cornerRadii) || undefined }}
        />
      )}

      {isFreeSlot && (
        <div
          className="w-full h-full border-2 border-dashed border-muted-foreground/50 flex items-center justify-center pointer-events-none"
          style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            borderRadius: cornerRadiiToCss(element.cornerRadii) || (element.isCircle ? '50%' : undefined),
          }}
        >
          <span className="text-xs text-muted-foreground/70">Photo</span>
        </div>
      )}

      {showSelected && !isLocked && !isTrombiText && !isFreeSlot && (
        <>
          {!isInMultiSelection && resizeHandles.map((corner) => (
            <div
              key={corner}
              data-resize-corner={corner}
              className={mobileInteraction ? 'resize-handle-mobile' : 'resize-handle'}
              style={mobileInteraction ? {
                ...handlePositions[corner],
                top: corner.includes('t') ? -22 / scale : undefined,
                bottom: corner.includes('b') ? -22 / scale : undefined,
                left: corner.includes('l') ? -22 / scale : undefined,
                right: corner.includes('r') ? -22 / scale : undefined,
                width: 44 / scale,
                height: 44 / scale,
                '--resize-dot-size': `${14 / scale}px`,
                '--resize-dot-border': `${2 / scale}px`,
              } as React.CSSProperties : handlePositions[corner]}
              onPointerDown={(e) => handleResizePointerDown(e, corner)}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          ))}

          {/* Rotation handle — visible only for rotatable elements (mobile: never on text, the dot gets in the way) */}
          {isRotatable && !isInMultiSelection && !(mobileInteraction && element.type === 'text') && (
            <div
              className="absolute flex items-center justify-center"
              style={{
                top: -28,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 18,
                height: 18,
                cursor: isRotating ? 'grabbing' : 'grab',
                zIndex: 60,
              }}
              onMouseDown={handleRotateMouseDown}
              title="Faire pivoter (Shift = pas de 15°)"
            >
              <div
                className="absolute"
                style={{
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 1,
                  height: 10,
                  backgroundColor: 'hsl(var(--editor-sidebar-active))',
                  opacity: 0.7,
                }}
              />
              <div
                className="w-3.5 h-3.5 rounded-full border-2 bg-background"
                style={{ borderColor: 'hsl(var(--editor-sidebar-active))' }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(DraggableElement);